import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Globe, MapPin, Clock, ArrowLeft, ShieldCheck, Star, X, Mail } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { VerificationBadge } from "../components/VerificationBadge";
import { waLink } from "../components/BusinessCard";
import { SponsorModal } from "../components/SponsorModal";

export default function BusinessDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [sponsorOpen, setSponsorOpen] = useState(false);

  useEffect(() => {
    api.business(id).then((d) => { setB(d); api.track({ business_id: id, type: "view" }); }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <Empty />;
  if (!b) return <div className="py-24 text-center text-muted-foreground">Cargando...</div>;

  const wa = waLink(b.whatsapp || b.phone, b.name);

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6 lg:px-8">
      <Link to="/explorar" data-testid="back-link" className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
        <ArrowLeft className="h-4 w-4" /> Volver
      </Link>

      <div className="mt-4 overflow-hidden rounded-3xl border border-border bg-card ml-fade-up">
        <div className="relative h-52 w-full bg-muted sm:h-72">
          {b.image_url && <img src={mediaUrl(b.image_url)} alt={b.name} className="h-full w-full object-cover" />}
          {b.featured && (
            <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-bold text-white">
              <Star className="h-3 w-3 fill-white" /> Destacado
            </span>
          )}
        </div>
        <div className="p-5 sm:p-7">
          <div className="flex flex-wrap items-center gap-2">
            <VerificationBadge status={b.verification_status} />
            <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold capitalize text-brand-ink">{b.category}</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-brand-ink sm:text-4xl">{b.name}</h1>
          <p className="mt-3 text-muted-foreground">{b.description}</p>

          <div className="mt-5 space-y-2.5 text-sm">
            {b.address && <Row icon={MapPin}>{b.address}, {b.city}, {b.state}</Row>}
            {!b.address && <Row icon={MapPin}>{b.city}, {b.state}</Row>}
            {b.hours && <Row icon={Clock}>{b.hours}</Row>}
            {b.phone && <Row icon={Phone}><a href={`tel:${b.phone}`} className="hover:text-brand">{b.phone}</a></Row>}
            {b.website && <Row icon={Globe}><a href={b.website} target="_blank" rel="noreferrer" className="break-all hover:text-brand">{b.website}</a></Row>}
          </div>

          {/* CTAs */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {b.phone && <CTA href={`tel:${b.phone}`} testid="detail-call" icon={Phone} label="Llamar" onClick={() => api.track({ business_id: id, type: "call" })} />}
            {wa && <CTA href={wa} ext testid="detail-whatsapp" label="WhatsApp" className="bg-whatsapp text-white hover:bg-whatsapp" onClick={() => api.track({ business_id: id, type: "whatsapp" })}
              icon={() => <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24z"/></svg>} />}
            {b.website && <CTA href={b.website} ext testid="detail-website" icon={Globe} label="Sitio web" onClick={() => api.track({ business_id: id, type: "website" })} />}
            <CTA as="button" testid="detail-directions" icon={MapPin} label="Cómo llegar" onClick={() => { api.track({ business_id: id, type: "directions" }); window.open(`https://maps.google.com/?q=${encodeURIComponent((b.address||"")+" "+b.city+" "+b.state)}`, "_blank"); }} />
          </div>

          {/* Owner actions */}
          <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-secondary/60 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm">
              <ShieldCheck className="h-5 w-5 text-brand" />
              <span className="font-semibold">{b.claimed ? "Negocio reclamado por su dueño" : "¿Es tu negocio?"}</span>
            </div>
            <div className="flex gap-2">
              {!b.claimed && (
                <button data-testid="claim-button" onClick={() => setClaimOpen(true)} className="rounded-full bg-brand px-5 py-2 text-sm font-bold text-white hover:bg-brand-hover">
                  Reclamar ficha
                </button>
              )}
              <button data-testid="sponsor-button" onClick={() => setSponsorOpen(true)} className="rounded-full border border-brand/30 px-5 py-2 text-sm font-bold text-brand hover:bg-brand hover:text-white">
                Destacar
              </button>
            </div>
          </div>

          <p className="mt-4 font-mono text-[11px] text-muted-foreground">
            Confianza {b.confidence_score}% · Fuente: {(b.source_urls || [])[0] || "n/d"}
          </p>
        </div>
      </div>

      <ClaimModal open={claimOpen} onClose={() => setClaimOpen(false)} business={b} />
      <SponsorModal open={sponsorOpen} onClose={() => setSponsorOpen(false)} business={b} />
    </div>
  );
}

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2.5 text-brand-ink"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand" /><span>{children}</span></div>
);

const CTA = ({ as, href, ext, icon: Icon, label, onClick, testid, className = "" }) => {
  const cls = `inline-flex h-12 flex-col items-center justify-center gap-0.5 rounded-xl bg-secondary text-xs font-bold text-brand-ink transition-colors hover:bg-brand hover:text-white ${className}`;
  const inner = <><Icon className="h-5 w-5" />{label}</>;
  if (as === "button") return <button data-testid={testid} onClick={onClick} className={cls}>{inner}</button>;
  return <a data-testid={testid} href={href} onClick={onClick} {...(ext ? { target: "_blank", rel: "noreferrer" } : {})} className={cls}>{inner}</a>;
};

const Empty = () => (
  <div className="flex flex-col items-center py-24 text-center">
    <p className="font-display text-xl font-bold">Negocio no encontrado</p>
    <Link to="/explorar" className="mt-3 rounded-full bg-brand px-5 py-2 text-sm font-bold text-white">Explorar directorio</Link>
  </div>
);

const ClaimModal = ({ open, onClose, business }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.createClaim({ business_id: business.id, email, origin_url: window.location.origin });
      toast.success(res.message || "Revisa tu correo.");
      onClose();
    } catch { toast.error("No se pudo enviar el correo."); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-4" onClick={onClose}>
      <div data-testid="claim-modal" className="w-full rounded-t-3xl bg-white p-6 sm:max-w-md sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">Reclamar {business.name}</h3>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-secondary"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Te enviaremos un enlace de verificación por email para confirmar que eres el dueño.</p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="claim-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" className="h-12 w-full rounded-xl border border-input bg-white pl-9 pr-3 outline-none focus:ring-2 focus:ring-brand/40" />
          </div>
          <button data-testid="claim-submit" disabled={busy} className="h-12 w-full rounded-full bg-brand font-bold text-white hover:bg-brand-hover disabled:opacity-60">
            {busy ? "Enviando..." : "Enviar verificación"}
          </button>
        </form>
      </div>
    </div>
  );
};
